import {
	memo,
	useCallback,
	useMemo,
	type FC,
	type ReactNode,
	useEffect,
	useRef,
	useState,
} from 'react';

import { css, keyframes } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { Link } from '@/libraries/design/components/link/src/link';
import { PlayPauseControls } from '@/libraries/design/components/play-pause-controls/src/play-pause-controls';
import { wacToken } from '@/libraries/design/foundations';
import { DataEventProps } from '@/libraries/wpl/foundations/src/data-event-props';

const DEFAULT_CYCLE_DURATION_MS = 6000;

const prefersReducedMotion =
	typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface ValuePropCardData {
	/** Card title (displayed in all states) */
	title: string;
	/** Card description (displayed when card is active/playing) */
	description?: ReactNode;
	/** Optional link text (displayed when card is active/playing) */
	linkText?: string;
	/** Optional link URL */
	linkHref?: string;
	/** Image source URL. If both imageSrc and videoSrc are provided, imageSrc takes priority */
	imageSrc?: string;
	/** Video source URL (e.g. mp4). Used when imageSrc is not provided */
	videoSrc?: string;
	/** Alt text for the media (used as alt for images, aria-label for videos) */
	mediaAlt?: string;
}

interface ValuePropCardProps {
	activeVideoDurationMs?: number | null;
	isActiveVideoPlaying?: boolean;
	card: ValuePropCardData;
	isActive: boolean;
	index: number;
	dataEventProps?: DataEventProps;
	onActivate?: () => void;
	isPlaying?: boolean;
	onAdvance?: () => void;
	/** Duration of the fill animation in ms (default 6000) */
	cycleDurationMs?: number;
	onPlayPauseToggle?: () => void;
	/** Override the fill bar colour (default #1868db) */
	fillColor?: string;
}

const cardWrapperCss = css({
	position: 'relative',
});

const containerCss = css({
	display: 'flex',
	flexDirection: 'row',
	gap: token('space.200'),
	cursor: 'pointer',
	textAlign: 'left',
	width: '100%',
});

const inCardPlayPauseCss = css({
	position: 'static',
	marginBlockEnd: token('space.100'),
	[media.above.sm]: {
		position: 'absolute',
		marginBlockEnd: 0,
		insetInlineEnd: `calc(100% + ${token('space.100')})`, // 8px gap on sm
		insetBlockStart: '50%',
		transform: 'translateY(-50%)',
	},
	[media.above.md]: {
		insetInlineEnd: 'calc(100% + 16px)', // 16px gap on md+
	},
});

const cardContainerFocusCss = css({
	'&:focus-visible': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: '2px',
		borderRadius: '6px',
	},
});

const progressBarContainerCss = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	width: '6px',
	flexShrink: 0,
	borderRadius: wacToken('corner.radius.card'),
	overflow: 'hidden',
	position: 'relative',
	'@media (prefers-reduced-motion: no-preference)': {
		transition: `background-color ${wacToken('motion.duration.250ms')} ${wacToken(
			'motion.easing.ease40-in-out',
		)}, opacity ${wacToken('motion.duration.250ms')} ${wacToken('motion.easing.ease40-in-out')}`,
	},
});

const progressBarContainerActiveCss = css({
	backgroundColor: wacToken('color.extended.blue100'),
});

const progressBarContainerInactiveCss = css({
	backgroundColor: 'transparent',
});

const barVisibleCss = css({ opacity: 1 });
const barHiddenCss = css({ opacity: 0 });

const progressBarFillKeyframes = keyframes({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	from: { height: '0%' },
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	to: { height: '100%' },
});

const progressBarFillCss = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	width: '100%',
	height: 0,
	backgroundColor: wacToken('color.primary.blue'),
	animationName: progressBarFillKeyframes,
	animationTimingFunction: 'linear',
	animationFillMode: 'forwards',
});

const contentCss = css({
	display: 'flex',
	flexDirection: 'column',
	paddingBlock: token('space.100'),
	flex: 1,
});

const titleCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	margin: '0 !important',
	transition: `color ${wacToken('motion.duration.250ms')} ${wacToken(
		'motion.easing.ease40-in-out',
	)}`,
});

const inactiveTitleCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	color: `${wacToken('color.neutral.light500')} !important`,
});

const cardHoverTitleCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover .card-inactive-title, &:focus-within .card-inactive-title': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		color: `${wacToken('color.primary.blue')} !important`,
	},
});

const descriptionCss = css({
	margin: 0,
});

const linkContainerCss = css({
	paddingBlockStart: token('space.100'),
});

const bodyExpandCss = css({
	position: 'relative',
	display: 'grid',
	width: '100%',
	overflow: 'clip',
});

const bodyExpandActiveCss = css({
	gridTemplateRows: '1fr',
	opacity: 1,
	marginBlockStart: token('space.100'),
	'@media (prefers-reduced-motion: no-preference)': {
		transitionProperty: 'grid-template-rows, opacity, margin-block-start',
		transitionDuration: '600ms',
		transitionTimingFunction: wacToken('motion.easing.ease.cubic-out'),
	},
});

const bodyExpandInactiveCss = css({
	gridTemplateRows: '0fr',
	opacity: 0,
	marginBlockStart: '0',
});

const bodyInnerCss = css({
	position: 'relative',
	minHeight: 0,
	overflow: 'hidden',
});

const gradientMaskCss = css({
	position: 'absolute',
	insetInlineStart: 0,
	insetInlineEnd: 0,
	insetBlockEnd: 0,
	height: '28px',
	// eslint-disable-next-line local/ensure-wac-token-usage
	background: 'linear-gradient(to bottom, transparent, white)',
	pointerEvents: 'none',
	'@media (prefers-reduced-motion: no-preference)': {
		transition: 'opacity 300ms ease-out',
	},
});

const gradientMaskVisibleCss = css({ opacity: 1 });
const gradientMaskHiddenCss = css({ opacity: 0 });

interface FillBarState {
	showBar: boolean;
	fillDurationMs: number;
	isFillBarRunning: boolean;
}

interface FillBarOptions {
	card: ValuePropCardData;
	isActive: boolean;
	isPlaying: boolean;
	isActiveVideoPlaying: boolean;
	activeVideoDurationMs: number | null;
	cycleDurationMs: number;
}

function computeFillBarState({
	card,
	isActive,
	isPlaying,
	isActiveVideoPlaying,
	activeVideoDurationMs,
	cycleDurationMs,
}: FillBarOptions): FillBarState {
	const showBar = isActive;
	const fillDurationMs =
		card.videoSrc && activeVideoDurationMs ? activeVideoDurationMs : cycleDurationMs;
	const isFillBarRunning = card.videoSrc ? isPlaying && isActiveVideoPlaying : isPlaying;

	return { showBar, fillDurationMs, isFillBarRunning };
}

interface CardBodyProps {
	card: ValuePropCardData;
	isActive: boolean;
	dataEventProps?: DataEventProps;
}

const CardBody = memo(({ card, isActive, dataEventProps }: CardBodyProps) => {
	const [isTransitioning, setIsTransitioning] = useState(false);
	const isFirstRender = useRef(true);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		if (prefersReducedMotion) return;
		setIsTransitioning(true);
		const t = setTimeout(() => setIsTransitioning(false), 600);
		return () => clearTimeout(t);
	}, [isActive]);

	return (
		<div css={contentCss}>
			<p
				className={`card-title-sm${!isActive ? ' card-inactive-title' : ''}`}
				css={[titleCss, !isActive && inactiveTitleCss]}
			>
				{card.title}
			</p>
			<div
				css={[bodyExpandCss, isActive ? bodyExpandActiveCss : bodyExpandInactiveCss]}
				aria-hidden={!isActive}
			>
				<div css={bodyInnerCss}>
					{card.description && (
						<div className="body-md" css={descriptionCss}>
							{card.description}
						</div>
					)}
					{isActive && card.linkText && card.linkHref && (
						<div css={linkContainerCss}>
							<Link
								aria-label={card.linkText}
								text={card.linkText}
								href={card.linkHref}
								type="primary"
								dataEventProps={dataEventProps}
								anchorProps={{ tabIndex: 0 }}
							/>
						</div>
					)}
				</div>
				<div
					css={[gradientMaskCss, isTransitioning ? gradientMaskVisibleCss : gradientMaskHiddenCss]}
					aria-hidden={true}
					data-testid="value-prop-gradient-mask"
				/>
			</div>
		</div>
	);
});

function buildInteractiveProps(
	isActive: boolean,
	title: string,
	onActivate: (() => void) | undefined,
	handleKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void,
) {
	if (isActive) {
		return { 'aria-label': title };
	}
	return {
		role: 'button' as const,
		tabIndex: 0,
		'aria-pressed': false as const,
		'aria-label': title,
		onClick: onActivate,
		onKeyDown: handleKeyDown,
	};
}

export const ValuePropCard: FC<ValuePropCardProps> = ({
	card,
	isActive,
	index,
	dataEventProps,
	onActivate,
	isPlaying = true,
	onAdvance,
	cycleDurationMs = DEFAULT_CYCLE_DURATION_MS,
	activeVideoDurationMs = null,
	isActiveVideoPlaying = false,
	onPlayPauseToggle,
	fillColor,
}) => {
	const { showBar, fillDurationMs, isFillBarRunning } = computeFillBarState({
		card,
		isActive,
		isPlaying,
		isActiveVideoPlaying,
		activeVideoDurationMs,
		cycleDurationMs,
	});

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				onActivate?.();
			}
		},
		[onActivate],
	);

	const interactiveProps = useMemo(
		() => buildInteractiveProps(isActive, card.title, onActivate, handleKeyDown),
		[isActive, card.title, onActivate, handleKeyDown],
	);

	return (
		<div css={[cardWrapperCss, !isActive && cardHoverTitleCss]}>
			{isActive && onPlayPauseToggle && (
				<div
					css={inCardPlayPauseCss}
					ref={(el) => {
						const button = el?.querySelector<HTMLButtonElement>('button');
						if (button) button.tabIndex = 0;
					}}
				>
					<PlayPauseControls
						isPlaying={Boolean(isPlaying)}
						onToggle={onPlayPauseToggle}
						mediaLabel={card.title}
					/>
				</div>
			)}
			<div
				css={[containerCss, cardContainerFocusCss]}
				data-testid={`value-prop-card-${index}`}
				data-active={isActive}
				{...interactiveProps}
			>
				<div
					css={[
						progressBarContainerCss,
						isActive && progressBarContainerActiveCss,
						!isActive && progressBarContainerInactiveCss,
						showBar && barVisibleCss,
						!showBar && barHiddenCss,
					]}
					aria-hidden={!showBar}
				>
					{isActive && (
						<div
							css={progressBarFillCss}
							style={{
								animationDuration: `${fillDurationMs}ms`,
								animationPlayState: isFillBarRunning ? 'running' : 'paused',
								...(fillColor ? { backgroundColor: fillColor } : undefined),
							}}
							onAnimationEnd={onAdvance}
						/>
					)}
				</div>
				<CardBody card={card} isActive={isActive} dataEventProps={dataEventProps} />
			</div>
		</div>
	);
};
